/* global Razorpay */
import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { useSelector, useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { setPaymentCompleted, setCurrentStep, setUserProfile } from "../../redux/onboardingReducer"
import { showToast } from "../../utils/toastUtils"
import { HoverBorderGradient } from "../../components/ui/hover-border-gradient"

import {
  Volume2,
  Building,
  User,
  MessageSquare,
  Mic,
  Sliders,
  CreditCard,
  PenIcon as UserPen,
  CheckCircle2,
  IndianRupeeIcon,
} from "lucide-react"
import { TextGenerateEffect } from "../../components/ui/text-generate-effect"
import OnboardingButtonDock from "../../components/OnboardingButtonDock"
import { useTheme } from "@mui/material/styles"

const OnboardingSummary = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const theme = useTheme()
  const isDarkMode = theme.palette.mode === "dark"

  const onboardingState = useSelector((state) => state.onboarding)
  const profile = onboardingState.profile || {}
  const user = profile.user || {}
  const organization = profile.organization || {}
  const botConfig = onboardingState.botConfig || {}
  const useCase = onboardingState.useCase || ""
  const integrations = onboardingState.integrations || []
  const pricing = onboardingState.pricing || {}

  useEffect(() => {
    const fetchUserProfile = async () => {
      setLoading(true)
      try {
        const userId = localStorage.getItem("userId")
        if (!userId) {
          window.location.href = 'https://www.verbalyze.in/login';
          throw new Error("User not found. Please log in again.")
        }
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user/${userId}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
        })
        if (!response.ok) {
          throw new Error("Failed to fetch user profile")
        }
        const userData = await response.json()
        dispatch(
          setUserProfile({
            user: {
              firstName: userData.firstName,
              lastName: userData.lastName,
              email: userData.email,
              phone: userData.phone,
              userId: userData.userId,
            },
            organization: {
              organizationName: userData.organisation,
              industry: userData.industry,
              size: userData.size,
              website: userData.website,
            },
          }),
        )
      } catch (error) {
        console.error(`Failed to load user profile:`, error)
        showToast(error.message, "error")
        window.location.href = 'https://www.verbalyze.in/login';
      } finally {
        setLoading(false)
      }
    }

    fetchUserProfile()
  }, [dispatch])

  const handleEditProfile = () => {
    dispatch(setCurrentStep(0))
    navigate('/onboarding')
  }

  const renderValue = (value) => {
    if (Array.isArray(value)) {
      return value.join(", ")
    }
    if (typeof value === "object" && value !== null) {
      return JSON.stringify(value)
    }
    return value || "Normal"
  }

  const formatName = (firstName, lastName) => {
    if (!firstName && !lastName) return "Not provided"
    const formattedFirstName = firstName ? firstName.charAt(0).toUpperCase() + firstName.slice(1) : ""
    const formattedLastName = lastName ? lastName.charAt(0).toUpperCase() + lastName.slice(1) : ""
    return `${formattedFirstName} ${formattedLastName}`.trim()
  }

  const capitalizeFirstLetter = (string) => {
    if (typeof string !== "string" || string.length === 0) return ""
    return string.charAt(0).toUpperCase() + string.slice(1)
  }

  const capitalizeFullName = (firstName, lastName) => {
    const capitalizedFirstName = capitalizeFirstLetter(firstName)
    const capitalizedLastName = capitalizeFirstLetter(lastName)
    return `${capitalizedFirstName} ${capitalizedLastName}`.trim()
  }

  const getVoiceFriendlyName = (voiceId) => {
    const voiceMapping = {
      "da-DK-Neural2-D": "Clara",
      "da-DK-Neural2-F": "Sofia",
      "de-DE-Neural2-A": "Hannah",
      "de-DE-Neural2-B": "Paul",
      "de-DE-Neural2-C": "Mia",
      "de-DE-Neural2-D": "Leon",
      "de-DE-Neural2-F": "Emma",
      "de-DE-Neural2-G": "Emilia",
      "de-DE-Neural2-H": "Noah",
      "en-AU-Neural2-A": "Olivia",
      "en-AU-Neural2-B": "William",
      "en-AU-Neural2-C": "Amelia",
      "en-AU-Neural2-D": "Jack",
      "en-GB-Neural2-A": "Olivia",
      "en-GB-Neural2-B": "Oliver",
      "en-GB-Neural2-C": "Amelia",
      "en-GB-Neural2-D": "George",
      "en-GB-Neural2-F": "Isla",
      "en-GB-Neural2-N": "Mia",
      "en-GB-Neural2-O": "Noah",
      "en-IN-Neural2-A": "Aaradhya",
      "en-IN-Neural2-B": "Arjun",
      "en-IN-Neural2-C": "Vivaan",
      "en-IN-Neural2-D": "Ananya",
      "en-US-Neural2-A": "Liam",
      "en-US-Neural2-C": "Olivia",
      "en-US-Neural2-D": "James",
      "en-US-Neural2-E": "Emma",
      "en-US-Neural2-F": "Charlotte",
      "en-US-Neural2-G": "Amelia",
      "en-US-Neural2-H": "Sophia",
      "en-US-Neural2-I": "Ethan",
      "en-US-Neural2-J": "Alexander",
      "es-ES-Neural2-A": "Sofía",
      "es-ES-Neural2-B": "Álvaro",
      "es-ES-Neural2-C": "María",
      "es-ES-Neural2-D": "Lucía",
      "es-ES-Neural2-E": "Carmen",
      "es-ES-Neural2-F": "Carlos",
      "es-ES-Neural2-G": "Antonio",
      "es-ES-Neural2-H": "Isabel",
      "es-US-Neural2-A": "Isabella",
      "es-US-Neural2-B": "Mateo",
      "es-US-Neural2-C": "Luis",
      "fil-ph-Neural2-A": "Maria",
      "fil-ph-Neural2-D": "Jose",
      "fr-CA-Neural2-A": "Élise",
      "fr-CA-Neural2-B": "Gabriel",
      "fr-CA-Neural2-C": "Chloé",
      "fr-CA-Neural2-D": "Léo",
      "fr-FR-Neural2-A": "Emma",
      "fr-FR-Neural2-B": "Lucas",
      "fr-FR-Neural2-C": "Sophie",
      "fr-FR-Neural2-D": "Hugo",
      "fr-FR-Neural2-E": "Chloé",
      "fr-FR-Neural2-F": "Julie",
      "fr-FR-Neural2-G": "Pierre",
      "hi-IN-Neural2-A": "Aaradhya",
      "hi-IN-Neural2-B": "Arjun",
      "hi-IN-Neural2-C": "Vivaan",
      "hi-IN-Neural2-D": "Ananya",
      "it-IT-Neural2-A": "Giulia",
      "it-IT-Neural2-C": "Lorenzo",
      "it-IT-Neural2-F": "Matteo",
      "ja-JP-Neural2-B": "Sakura",
      "ja-JP-Neural2-C": "Hiroshi",
      "ja-JP-Neural2-D": "Yuki",
      "ko-KR-Neural2-A": "Jiwoo",
      "ko-KR-Neural2-B": "Jiwon",
      "ko-KR-Neural2-C": "Minho",
      "pt-BR-Neural2-A": "Ana",
      "pt-BR-Neural2-B": "Carlos",
      "pt-BR-Neural2-C": "Fernanda",
      "th-TH-Neural2-C": "Suda",
      "vi-VN-Neural2-A": "Mai",
      "vi-VN-Neural2-D": "Minh",
    }

    return voiceMapping[voiceId] || voiceId
  }

  const renderNeuralVoiceName = (voice) => {
    if (typeof voice === "string") {
      return getVoiceFriendlyName(voice)
    }
    if (typeof voice === "object" && voice !== null) {
      return getVoiceFriendlyName(voice.name || voice.voice)
    }
    return "Not set"
  }

  const getLanguageName = (languageCode) => {
    const languageMapping = {
      "de-DE": "German (Germany)",
      "en-AU": "English (Australia)",
      "en-GB": "English (United Kingdom)",
      "en-IN": "English (India)",
      "en-US": "English (United States)",
      "es-ES": "Spanish (Spain)",
      "es-US": "Spanish (United States)",
      "fil-PH": "Filipino (Philippines)",
      "fr-CA": "French (Canada)",
      "fr-FR": "French (France)",
      "hi-IN": "Hindi (India)",
      "it-IT": "Italian (Italy)",
      "ja-JP": "Japanese (Japan)",
      "ko-KR": "Korean (South Korea)",
      "pt-BR": "Portuguese (Brazil)",
      "th-TH": "Thai (Thailand)",
      "vi-VN": "Vietnamese (Vietnam)",
      "da-DK": "Danish (Denmark)",
    }
    return languageMapping[languageCode] || languageCode
  }

  const firstName = profile?.user?.firstName || ""
  const lastName = profile?.user?.lastName || ""
  const fullName = capitalizeFullName(firstName, lastName)

  const paymentHandler = async () => {
    try {
      //Calling the backend to create an order
      const userId = localStorage.getItem("userId");
      if (!userId) {
        throw new Error("User not found. Please log in again.");
      }

      const priceWithoutGST = pricing.price;
      const gstAmount = Math.round(priceWithoutGST * 0.18);
      const totalAmount = priceWithoutGST + gstAmount;

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/payment/order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify({ 
          amount: totalAmount * 100, // Convert to paise
          userId: userId,
          currency: "INR",
          receipt: `receipt_${Date.now()}`,
          notes: {
            description: "Payment for AI Talker subscription",
            priceWithoutGST: priceWithoutGST,
            gstAmount: gstAmount
          }
        }),
      })

      if (!response.ok) {
        throw new Error("Failed to create order")
      }

      const { data: order } = await response.json()
      const razorpayKey = "rzp_test_hoKCmKm0sSq6xR"

      //Initialize Razorpay checkout options
      const options = {
        key: razorpayKey,
        amount: order.amount, // Amount in paise
        currency: "INR",
        name: "Verbalyze",
        description: `Payment for subscription (₹${priceWithoutGST} + ₹${gstAmount} GST)`,
        order_id: order.id,
        handler: (response) => {
          // Handle payment success

          // Sending payment details to backend for verification
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/payment/verify`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              userId: userId,
              priceWithoutGST: priceWithoutGST,
              gstAmount: gstAmount,
              totalAmount: totalAmount
            }),
          })
            .then((res) => res.json())
            .then((verificationResponse) => {
              if (verificationResponse.message === "Payment Successfully Verified") {
                showToast(`Payment of ₹${totalAmount} (including ₹${gstAmount} GST) Verified!`, "success")
                dispatch(setPaymentCompleted(true))
                navigate("/dashboard")
              } else {
                showToast("Payment verification failed. Please contact support.", "error")
              }
            })
            .catch((error) => {
              console.error("Payment verification error:", error)
              showToast("Payment verification failed. Please contact support.", "error")
            })
        },
        prefill: {
          name: `${user.firstName} ${user.lastName}`.trim(),
          email: user.email || "",
          contact: user.phone || "",
        },
        theme: {
          color: "#3b82f6",
        },
      }

      //  Opening Razorpay checkout
      const rzp1 = new window.Razorpay(options)
      rzp1.open()
    } catch (error) {
      console.error("Payment initiation error:", error)
      showToast("Failed to initiate payment. Please try again.", "error")
    }
  }

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1],
      },
    }),
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  }

  // Combined card data for profile and AI Talker
  const combinedCards = [
    {
      title: "User Profile",
      icon: <User className="w-5 h-5 text-violet-500" />,
      items: [
        { label: "Name", value: formatName(user.firstName, user.lastName) },
        { label: "Email", value: user.email || "Not provided" },
        { label: "Phone", value: user.phone || "Not provided" },
      ],
    },
    {
      title: "Organization",
      icon: <Building className="w-5 h-5 text-violet-500" />,
      items: [
        { label: "Name", value: capitalizeFirstLetter(organization.organizationName) || "Not provided" },
        { label: "Industry", value: renderValue(organization.industry) },
        { label: "Size", value: renderValue(organization.size) },
        { label: "Website", value: renderValue(organization.website) },
      ],
    },
    {
      title: "AI Talker General Information",
      icon: <MessageSquare className="w-5 h-5 text-violet-500" />,
      items: [
        { label: "AI Talker Name", value: renderValue(botConfig?.name) },
        { label: "Use Case", value: capitalizeFirstLetter(renderValue(useCase)) },
      ],
    },
    {
      title: "Voice Settings",
      icon: <Mic className="w-5 h-5 text-violet-500" />,
      items: [
        { label: "Language", value: getLanguageName(botConfig?.voice?.language) },
        { label: "Neural Voice", value: renderNeuralVoiceName(botConfig?.voice) },
        { label: "Pitch", value: renderValue(botConfig?.voice?.pitch) },
        { label: "Speaking Rate", value: renderValue(botConfig?.voice?.rate) },
      ],
    },
    {
      title: "Integrations",
      icon: <Sliders className="w-5 h-5 text-violet-500" />,
      chips: integrations && integrations.length > 0 ? integrations.map((integration) => renderValue(integration)) : [],
    },
  ]

  return (
    <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8`}>
      <div className={`${isDarkMode ? 'bg-slate-800' : 'bg-white'} rounded-xl p-6 mb-8`}>
        <div className="mb-6">
          <TextGenerateEffect
            words={user.firstName ? `Hey ${user.firstName},` : "Hello,"}
            className={`text-3xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-indigo-600 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}
          />

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} flex-1 text-lg leading-relaxed`}>
              Please review your AI Talker configuration before finalizing the onboarding process.
            </p>
            <HoverBorderGradient
              containerClassName="rounded-full"
              className="group relative rounded-full"
              as="button"
              onClick={paymentHandler}
              from="#4f46e5"
              to="#3b82f6"
              fromOpacity={0.5}
              toOpacity={0.8}
            >
              <div className="relative  bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 text-white font-medium py-3 px-8 rounded-full text-lg shadow-md flex items-center gap-2 group-hover:gap-3 transition-all hover:scale-105 hover:brightness-110 hover:shadow-lg">
                <span>Proceed to Pay</span>
                <CreditCard className="w-5 h-5 animate-pulse" />
              </div>
            </HoverBorderGradient>
          </div>
        </div>

        {/* Plan Information */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className={`mt-10 mb-8 ${
            isDarkMode ? 'bg-gradient-to-r from-blue-800 to-blue-900' : 'bg-gradient-to-r from-blue-600 to-blue-600'
          } rounded-2xl overflow-hidden shadow-lg`}
        >
          <div className="p-6 text-white">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex items-center">
                <div className="p-2 rounded-full bg-white/20 backdrop-blur-sm mr-3">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-violet-200 text-sm">Selected Plan</p>
                  <h3 className="text-xl font-bold">{capitalizeFirstLetter(renderValue(pricing?.plan))}</h3>
                </div>
              </div>
              <div className="flex items-center mt-2 sm:mt-0">
                <div className="p-2 rounded-full bg-white/20 backdrop-blur-sm mr-3">
                  <IndianRupeeIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-lg">Price</p>
                  <h3 className="text-2xl font-bold mb-1">₹{renderValue(pricing?.price)} + GST</h3>
                  <p className={`text-sm mb-1 ${isDarkMode ? 'text-slate-400' : ''}`}>GST (18%): {(Math.round(pricing?.price * 0.18))}</p>
                  <p className="text-md text-violet-200">
                    Total: ₹{pricing?.price + Math.round(pricing?.price * 0.18)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="mt-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-3 mb-6"
          >
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md cursor-pointer" onClick={handleEditProfile}>
              <UserPen className="w-5 h-5 text-white" />
            </div>
            <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Configuration Summary</h2>
          </motion.div>

          <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid gap-8">
            {/* User & Organization Section */}
            <motion.div
              variants={cardVariants}
              custom={0}
              className={`${
                isDarkMode ? 'bg-slate-800 border-gray-700' : 'bg-white border-gray-100'
              } rounded-2xl border shadow-md overflow-hidden`}
            >
              <div className={`${
                isDarkMode ? 'bg-gradient-to-r from-blue-800 to-blue-900' : 'bg-gradient-to-r from-blue-500 to-blue-600'
              } px-6 py-4`}>
                <h3 className="text-lg font-semibold text-white">Personal Information</h3>
              </div>

              <div className="p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className={`font-medium text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'} mb-4 flex items-center`}>
                      <User className="w-5 h-5 text-violet-600 t mr-2" />
                      User Profile
                    </h4>
                    <div className="space-y-4 space-x-5">
                      <div className="flex items-center pl-5">
                        <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} w-20`}>Name:</p>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{formatName(user.firstName, user.lastName)}</p>
                      </div>
                      <div className="flex items-center">
                        <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} w-20`}>Email:</p>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{user.email || "Not provided"}</p>
                      </div>
                      <div className="flex items-center">
                        <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} w-20`}>Phone:</p>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{user.phone || "Not provided"}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className={`font-medium text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'} mb-4 flex items-center`}>
                      <Building className="w-5 h-5 text-violet-600 mr-2" />
                      Organization
                    </h4>
                    <div className="space-y-2 space-x-5">
                      <div className="flex items-center pl-5">
                        <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} w-20`}>Name:</p>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          {capitalizeFirstLetter(organization.organizationName) || "Not provided"}
                        </p>
                      </div>
                      <div className="flex items-center">
                        <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} w-20`}>Industry:</p>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{renderValue(organization.industry)}</p>
                      </div>
                      <div className="flex items-center">
                        <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} w-20`}>Size:</p>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{renderValue(organization.size)}</p>
                      </div>
                      <div className="flex items-center">
                        <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} w-20`}>Website:</p>
                        <p className={`font-medium  ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{renderValue(organization.website)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* AI Talker Section */}
            <motion.div
              variants={cardVariants}
              custom={1}
              className={`${
                isDarkMode ? 'bg-slate-800 border-gray-700' : 'bg-white border-gray-100'
              } rounded-2xl border shadow-md overflow-hidden`}
            >
              <div className={`${
                isDarkMode ? 'bg-gradient-to-r from-blue-800 to-blue-900' : 'bg-gradient-to-r from-blue-500 to-blue-600'
              } px-6 py-4`}>
                <h3 className="text-lg font-semibold text-white">AI Talker Configuration</h3>
              </div>

              <div className="p-6">
                <div className="grid md:grid-cols-3 gap-26">
                  <div className="pl-2">
                    <h4 className={`font-medium text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'} mb-4 flex items-center`}>
                      <MessageSquare className="w-5 h-5 text-violet-600 mr-2" />
                      General Information
                    </h4>
                    <div className="space-y-4">
                      <div className="flex items-center pl-5">
                        <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} w-32`}>AI Talker Name:</p>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{renderValue(botConfig?.name)}</p>
                      </div>
                      <div className="flex items-center pl-5">
                        <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} w-32`}>Use Case:</p>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{capitalizeFirstLetter(renderValue(useCase))}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className={`font-medium text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'} mb-4 flex items-center`}>
                      <Mic className="w-5 h-5 text-violet-600 mr-2" />
                      Voice Settings
                    </h4>
                    <div className="space-y-4 ">
                      <div className="flex items-center pl-5">
                        <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} `}>Language:</p>
                        <p className={`font-medium pl-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{getLanguageName(botConfig?.voice?.language)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="pl-16">
                    <h4 className={`font-medium text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'} mb-4 flex items-center`}>
                      <Sliders className="w-5 h-5 text-violet-600 mr-2" />
                      Integrations
                    </h4>
                    <div className="space-y-4 pl-5">
                      {integrations && integrations.length > 0 ? (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {integrations.map((integration, i) => (
                            <motion.span
                              key={i}
                              className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
                                isDarkMode ? 'bg-violet-900 text-violet-100 border-violet-700' : 'bg-violet-100 text-violet-800 border-violet-200'
                              }`}
                              whileHover={{ scale: 1.05 }}
                              transition={{ type: "spring", stiffness: 400, damping: 17 }}
                            >
                              <Volume2 className="w-3.5 h-3.5 mr-1.5" />
                              {renderValue(integration)}
                            </motion.span>
                          ))}
                        </div>
                      ) : (
                        <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No integrations selected</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
      <OnboardingButtonDock
        onSave={paymentHandler}
        onBack={() => {
          dispatch(setCurrentStep(5))
        }}
        title="complete & pay"
        loading={false}
        desktopClassName="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50"
        mobileClassName="fixed bottom-8 right-8 z-50"
        showNextButton={false}
      />
    </div>
  )
}

export default OnboardingSummary
